import React, {useState, useEffect, useRef} from 'react'
import moment from 'moment'
import {Alert, Button, H6, Modal, ModalHeader, ModalActions} from '../UI/index.js'
import {formSpacing, FormStack, Input, Select} from '../UI/forms/index.js'
import type {SelectRef} from '../UI/forms/Select.js'
import type {InputRef} from '../UI/forms/Input.js'
import VehicleNotAvailable from './VehicleNotAvailable.js'
import useVehicleStore from '../../stores/VehicleStore.js'
import NoteEntry from './NoteEntry.js'
import {Vehicle} from '../../../server/functions/vehicles/vehicles.types.js'
import {Reseller, VehicleType} from '../../../types/types.js'
import {VehicleModel} from '../../../server/models/vehicle.js'
import t from 'src/helpers/Languages.js'
import {getAvailableBranches} from 'src/utils/branches.js'

interface EditVehicleModalProps {
    reseller: Reseller
    vehicle: Vehicle
    open: boolean
    onClose: () => void
    vehicleTypes?: VehicleType[]
    onChange: (vehicle: Vehicle) => void
    onArchive: () => void
    errors?: string[]
}

export default function EditVehicleModal({reseller, vehicle, open, onClose, vehicleTypes, onChange, onArchive, errors}: EditVehicleModalProps) {
    const vehicles = useVehicleStore((state) => state.vehicles)
    const [vehicleLocal, setVehicleLocal] = useState(structuredClone(vehicle))
    const [duplicateNameError, setDuplicateNameError] = useState('')
    const nameRef = useRef(null)
    const typeIdRef = useRef(null)

    const originalVehicleType = 'typeId' in vehicle && vehicleTypes?.length > 0 && vehicleTypes.find((type) => type._id === vehicle.typeId)

    const activeVehicleType = vehicleTypes?.length > 0 && vehicleTypes.find((type) => type._id === vehicleLocal.typeId)

    const onSave = () => {
        const requiredFields: {
            value: string
            ref: React.RefObject<SelectRef | InputRef>
            action?: () => void
        }[] = [
            {
                value: vehicleLocal.name,
                ref: nameRef
            },
            {
                value: vehicleLocal.typeId,
                ref: typeIdRef
            }
        ]

        let fieldsAreValid = true

        requiredFields.forEach((field) => {
            if (!field.value) {
                if (field.ref) {
                    field.ref.current?.setInvalid()
                }

                if (field.action) {
                    field.action()
                }

                fieldsAreValid = false
            }
        })

        const vehicleWithSameNameExists = vehicles.some((vehicle) => vehicle.name === vehicleLocal.name && vehicle._id !== vehicleLocal._id)

        if (vehicleWithSameNameExists) {
            setDuplicateNameError('Er bestaat al een voertuig met deze naam.')
            return
        }

        if (fieldsAreValid) {
            onChange(vehicleLocal)
        }
    }

    useEffect(() => {
        setVehicleLocal(structuredClone(vehicle))
    }, [vehicle])

    useEffect(() => {
        if (!open) {
            setDuplicateNameError('')
            setVehicleLocal(structuredClone(vehicle))
        } else {
            console.log(vehicle)
        }
    }, [open])

    return (
        <Modal show={open} onClose={onClose} style={{maxWidth: 600}}>
            <ModalHeader title='Voertuig' onClose={onClose} style={{alignItems: 'baseline'}} />

            {vehicleTypes?.length === 0 ?
                <Alert variant='warning' style={{marginBottom: 6, marginTop: 12}}>
                    Voertuigsoorten zijn niet beschikbaar. Voeg eerst een voertuigsoort toe.
                </Alert> :
                <>

                    <FormStack style={{marginBottom: formSpacing.rowGap}}>
                        {getAvailableBranches(reseller).length > 0 &&
                            <Select
                                label={t('branch')}
                                value={vehicleLocal.branchId || ''}
                                options={getAvailableBranches(reseller).map((branch) => {
                                    return {value: branch.id, title: branch.name}
                                })}
                                onChange={(event) => setVehicleLocal({...vehicleLocal, branchId: event.target.value})}
                                allowEmpty
                            />
                        }
                        <Input
                            label='Naam'
                            value={vehicleLocal.name}
                            onChange={(event) => {
                                setVehicleLocal({...vehicleLocal, name: event.target.value})
                                setDuplicateNameError('')
                            }}
                            ref={nameRef}
                            maxLength={VehicleModel.shape.name.maxLength}
                        />

                        {duplicateNameError && <Alert style={{marginBottom: 6}} variant='error'>{duplicateNameError}</Alert>}

                        <Select
                            label='Voertuigsoort'
                            value={vehicleLocal.typeId}
                            onChange={(event) => setVehicleLocal({...vehicleLocal, typeId: event.target.value})}
                            ref={typeIdRef}

                        >
                            {vehicleTypes.filter((v) => !v.isArchived || v._id === vehicleLocal.typeId).map((vehicleType) => (
                                <option disabled={vehicleType.isArchived} key={vehicleType._id} value={vehicleType._id}>{vehicleType.name}</option>
                            ))}
                        </Select>

                        <Input
                            label='Opmerking voor koerier'
                            value={vehicleLocal.comment}
                            placeholder={activeVehicleType?.comment}
                            onChange={(event) => setVehicleLocal({...vehicleLocal, comment: event.target.value})}
                        />

                        <Input
                            label='Notities'
                            value={vehicleLocal.notes}
                            placeholder={activeVehicleType?.notes}
                            onChange={(event) => setVehicleLocal({...vehicleLocal, notes: event.target.value})}
                        />
                    </FormStack>

                    <H6 style={{marginBottom: 12, marginTop: 24}}>Voertuig niet beschikbaar</H6>

                    <FormStack>
                        {vehicleLocal?.notAvailableDateTimeRanges?.map((range, index) => (
                            <VehicleNotAvailable
                                key={`vehicleNotAvailable-${index}`}
                                range={range}
                                onChange={(newRange) => {
                                    const ranges = vehicleLocal.notAvailableDateTimeRanges

                                    ranges[index] = newRange

                                    setVehicleLocal({...vehicleLocal, notAvailableDateTimeRanges: ranges})
                                }}
                                onRemove={() => {
                                    const ranges = [...vehicleLocal.notAvailableDateTimeRanges]

                                    ranges.splice(index, 1)

                                    setVehicleLocal({...vehicleLocal, notAvailableDateTimeRanges: ranges})
                                }}
                            />
                        ))}

                        <Button
                            variant='outline-white'
                            icon='mdi mdi-plus'
                            label='Reservering toevoegen'
                            onClick={() => setVehicleLocal({
                                ...vehicleLocal,
                                notAvailableDateTimeRanges: [
                                    ...(vehicleLocal.notAvailableDateTimeRanges || []),
                                    {
                                        start: '',
                                        end: '',
                                        reason: ''
                                    }
                                ]
                            })}
                        />
                    </FormStack>

                    <H6 style={{marginBottom: 12, marginTop: 24}}>Notities</H6>

                    <div style={{maxHeight: 320, overflowY: 'auto'}}>
                        {[...(vehicleLocal.notesEntries || [])].sort((a, b) => moment(b.date).diff(moment(a.date)))?.map((entry, index) => (
                            <NoteEntry
                                key={`noteEntry-${entry.note}-${entry.date}-${index}`}
                                entry={entry}
                                onChange={(updatedEntry) => {
                                    const entries = vehicleLocal.notesEntries

                                    entries[index] = updatedEntry

                                    entries.sort((a, b) => moment(b.date).diff(moment(a.date)))

                                    setVehicleLocal({...vehicleLocal, notesEntries: entries})
                                }}
                                onRemove={() => {
                                    const entries = [...vehicleLocal.notesEntries]

                                    entries.splice(index, 1)

                                    setVehicleLocal({...vehicleLocal, notesEntries: entries})
                                }}
                            />
                        ))}
                    </div>

                    <Button
                        variant='outline-white'
                        icon='mdi mdi-plus'
                        label='Notitie toevoegen'
                        onClick={() => setVehicleLocal({
                            ...vehicleLocal,
                            notesEntries: [
                                {
                                    date: '',
                                    note: ''
                                },
                                ...(vehicleLocal.notesEntries || [])
                            ]
                        })}
                        style={{marginBottom: 8}}
                    />

                    {errors?.length > 0 && errors.map((error, index) => (
                        <Alert style={{marginBottom: 6}} key={index} variant='error'>
                            {error}
                        </Alert>
                    ))
                    }

                    <ModalActions style={{marginTop: 24}}>
                        {vehicleLocal._id && !originalVehicleType?.isArchived ?
                            <Button onClick={onArchive}>
                                {vehicleLocal.isArchived ? 'Dearchiveren' : 'Archiveren'}
                            </Button> :
                            <Button onClick={onClose}>Annuleren</Button>
                        }

                        <Button
                            onClick={onSave}
                        >
                            Opslaan
                        </Button>
                    </ModalActions>
                </>
            }

        </Modal>
    )
}
